import { useState } from 'react';
import { X, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { useToast } from '../../context/ToastContext';

const TYPE_STYLES = {
  error:   'bg-rose-50 border border-rose-300 text-rose-900',
  success: 'bg-emerald-50 border border-emerald-300 text-emerald-900',
  warning: 'bg-amber-50 border border-amber-300 text-amber-900',
  info:    'bg-blue-50 border border-blue-300 text-blue-900',
};

const CLOSE_STYLES = {
  error:   'text-rose-500 hover:text-rose-700',
  success: 'text-emerald-500 hover:text-emerald-700',
  warning: 'text-amber-500 hover:text-amber-700',
  info:    'text-blue-500 hover:text-blue-700',
};

// Build the copyable operator block from the raw detail (status + cause + ref).
const buildDetailText = (detail) =>
  [
    detail?.status ? `Status: ${detail.status}` : null,
    detail?.raw ? `Detail: ${detail.raw}` : null,
    detail?.correlationId ? `Ref: ${detail.correlationId}` : null,
  ]
    .filter(Boolean)
    .join('\n');

const ToastItem = ({ toast, onClose }) => {
  const [open, setOpen] = useState(false);
  const typeStyle  = TYPE_STYLES[toast.type]  ?? TYPE_STYLES.info;
  const closeStyle = CLOSE_STYLES[toast.type] ?? CLOSE_STYLES.info;

  const detailText = buildDetailText(toast.detail);
  // Operator-only affordance: only when there's a real raw cause to show.
  const hasDetail = Boolean(toast.detail?.raw);

  const copyDetail = () => {
    try { navigator.clipboard?.writeText(detailText); } catch { /* clipboard unavailable */ }
  };

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 rounded-lg px-4 py-3 shadow-md min-w-[260px] max-w-sm opacity-100 transition-opacity duration-300 ${typeStyle}`}
    >
      <div className="flex-1 min-w-0">
        <span className="block text-sm font-medium whitespace-pre-line">{toast.message}</span>

        {hasDetail && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              className="inline-flex items-center gap-1 text-xs opacity-60 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 rounded"
            >
              {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              Details
            </button>

            {open && (
              <div className="mt-1 rounded border border-black/10 bg-black/5 p-2">
                <pre className="m-0 whitespace-pre-wrap break-words font-mono text-[11px] leading-snug opacity-80">{detailText}</pre>
                <button
                  type="button"
                  onClick={copyDetail}
                  className="mt-1 inline-flex items-center gap-1 text-[11px] opacity-60 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 rounded"
                >
                  <Copy size={11} /> Copy
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <button
        onClick={onClose}
        aria-label="Dismiss notification"
        className={`shrink-0 mt-0.5 ${closeStyle} focus:outline-none focus:ring-2 focus:ring-offset-1 rounded`}
      >
        <X size={16} />
      </button>
    </div>
  );
};

const Toast = () => {
  const { toasts, removeToast } = useToast();

  if (!toasts || toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex max-h-[calc(100vh-2rem)] flex-col gap-2 overflow-y-auto">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

export default Toast;
